/* eslint-disable import/prefer-default-export */
/**
 * Merge UserContext `abTests` with the URL `abtest` query value:
 * trim tokens, dedupe, sort alphabetically (locale-aware), join with comma.
 * The sentinel value `none` is dropped when other tests are present.
 *
 * @param {string[]} abTests - Active A/B tests from useUserContext (from `ab` param + addAbTest).
 * @param {string|undefined} urlAbtestRaw - Raw `abtest` query value (may contain %2C).
 * @param {boolean} abtestInParams - Whether an `abtest` key exists in the URL (even if empty).
 * @returns {string} Comma-separated sorted list, `'none'` when there are no tests, or `''` when
 *   `abtest` is present but empty and there are no other tokens (legacy URLSearchParams behavior).
 */
export function mergeDedupeSortAbTests(abTests, urlAbtestRaw, abtestInParams) {
  const tokenSet = new Set();

  const addFromCommaString = (raw) => {
    if (raw == null || raw === '') return;
    let s = String(raw);
    if (s.includes('%2C')) {
      s = s.split('%2C').join(',');
    }
    s.split(',').forEach((part) => {
      const t = part.trim();
      if (t) tokenSet.add(t);
    });
  };

  if (Array.isArray(abTests)) {
    abTests.forEach((item) => {
      const t = String(item).trim();
      if (t) tokenSet.add(t);
    });
  }

  addFromCommaString(urlAbtestRaw);

  if (tokenSet.size > 1 && tokenSet.has('none')) {
    tokenSet.delete('none');
  }

  if (tokenSet.size === 0) {
    const urlRaw = urlAbtestRaw == null ? '' : String(urlAbtestRaw);
    if (abtestInParams && urlRaw.trim() === '') {
      return '';
    }
    return 'none';
  }

  if (tokenSet.size === 1 && tokenSet.has('none')) {
    return 'none';
  }

  const sorted = [...tokenSet]
    .filter((t) => t !== 'none')
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

  if (sorted.length === 0) {
    return 'none';
  }

  return sorted.join(',');
}
